type SfxName = "kick" | "charge" | "bounce" | "goal" | "post" | "join" | "leave";

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private enabled = true;
  private volume = 0.5;

  private ensure(): void {
    if (this.ctx) return;
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.volume;
    this.master.connect(this.ctx.destination);
  }

  resume(): void {
    this.ensure();
    if (this.ctx && this.ctx.state === "suspended") void this.ctx.resume();
  }

  setEnabled(v: boolean): void { this.enabled = v; }

  /** Volumen maestro 0..1. Aplica en caliente si el contexto ya existe. */
  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this.volume;
  }

  private noiseBuffer(dur: number): AudioBuffer {
    const ctx = this.ctx!;
    const len = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    return buf;
  }

  private blip(freq: number, dur: number, type: OscillatorType, gain: number, slideTo?: number): void {
    if (!this.enabled) return;
    this.ensure();
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (slideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(1, slideTo), t + dur);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g);
    g.connect(this.master!);
    osc.start(t);
    osc.stop(t + dur);
  }

  private thud(gain: number, dur: number): void {
    if (!this.enabled) return;
    this.ensure();
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer(dur);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 900;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.master!);
    src.start(t);
    src.stop(t + dur);
  }

  play(name: SfxName, intensity = 1): void {
    if (!this.enabled) return;
    this.ensure();
    switch (name) {
      case "kick":
        this.blip(180, 0.1, "square", 0.3 * intensity, 70);
        this.blip(440, 0.05, "sawtooth", 0.15 * intensity, 220);
        this.thud(0.22 * intensity, 0.07);
        break;
      case "bounce":
        this.thud(0.08 * Math.min(1, intensity), 0.04);
        break;
      case "post":
        this.blip(880, 0.07, "triangle", 0.2, 1200);
        break;
      case "goal":
        this.blip(523, 0.12, "square", 0.3);
        this.blip(659, 0.12, "square", 0.3);
        this.blip(784, 0.18, "square", 0.3);
        break;
      // Entrar y salir de la sala suenan como lo que son: dos notas que SUBEN
      // al llegar alguien y BAJAN al irse. Que se distingan sin mirar es todo el
      // punto — quien está jugando no puede apartar la vista para leer el chat.
      case "join":
        this.blip(660, 0.09, "triangle", 0.16, 990);
        break;
      case "leave":
        this.blip(520, 0.11, "triangle", 0.14, 330);
        break;
    }
  }

  startCharge(): void { /* sonido de carga deshabilitado por diseño */ }

  stopCharge(): void { /* sonido de carga deshabilitado por diseño */ }
}
